import { useCallback, useEffect, useRef, type ReactNode } from 'react'

/**
 * The off-canvas navigation drawer — one implementation, every shell that has a
 * sidebar it cannot afford to draw.
 *
 * 🔴 WHY A SIDEBAR HAS TO BECOME SOMETHING ELSE RATHER THAN JUST GET NARROWER.
 * `DashboardShell`'s column is `w-[15.5rem]` — 248px. On the 360px phone the
 * parent and school tiers are read on, that is 69% of the window, and what is
 * left is 112px of "content": a roster table showing most of one column, a stat
 * grid one tile wide. There is no width the sidebar could shrink to that leaves
 * both it and the page usable, because the page needs all of it.
 *
 * ⚠️ AND IT IS NOT THE SAME ANSWER AS THE PARENT TIER'S THUMB BAR. That bar
 * exists because a parent has six destinations and *"30 seconds on a phone"*
 * (information-architecture.md §2) — navigation they will use every visit, so it
 * is worth permanent room at the reachable edge. A head teacher's console has
 * seven items they touch once and then work for ten minutes; permanent chrome
 * for that is rent charged against the table they came to read. One is a bar,
 * one is a drawer, and the difference is how often the reader needs it.
 *
 * ── What this owns, and what CSS owns ─────────────────────────────────────
 *
 * The shells decide WHEN a drawer exists (`lg:hidden` and friends) and what
 * goes in it. This owns the four behaviours a class cannot express, each of
 * which is a bug if it is missing:
 *
 *   · Escape closes it            — the only exit a keyboard reader has, since
 *                                   the backdrop is not focusable.
 *   · Focus moves in, and back    — without it, tabbing from the menu button
 *                                   walks the page BEHIND the drawer, and a
 *                                   screen-reader user is reading a page they
 *                                   cannot see.
 *   · Focus is trapped            — same reason, from the other end.
 *   · The page behind is inert    — `aria-hidden` on the backdrop is not enough;
 *                                   the rubber-band scroll leaks through on iOS.
 */
export interface DrawerProps {
  open: boolean
  onClose: () => void
  /**
   * What a screen reader is told this panel is. Required — an unlabelled dialog
   * is announced as "dialog" and nothing else.
   */
  label: string
  /**
   * The side it slides from. Navigation comes from the left because that is
   * where the sidebar it replaces lives, and a drawer that arrives somewhere
   * other than where its trigger points reads as a different control.
   *
   * ⚠️ `bottom` IS THE SAME RULE APPLIED TO THE THUMB BAR. Its trigger sits at
   * the bottom edge, so the sheet rises from there — full width, under the
   * thumb that opened it, and clear of the home indicator. `width` is ignored.
   */
  side?: 'left' | 'right' | 'bottom'
  /** Panel width. Defaults to the sidebar's own 15.5rem, capped to the phone. */
  width?: string
  children: ReactNode
}

/**
 * Everything focusable, in document order. Used for the trap's two ends —
 * exported because `ModalCard` traps focus for the same reason and a second
 * copy of this selector is a second chance for one of them to drift.
 */
export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Drawer({
  open,
  onClose,
  label,
  side = 'left',
  width = 'w-[15.5rem]',
  children,
}: DrawerProps) {
  const panel = useRef<HTMLDivElement>(null)
  /*
   * ⚠️ CAPTURED WHEN THE DRAWER OPENS, NOT WHEN IT CLOSES. By the time it
   * closes, focus is inside a panel that is about to be removed from the
   * document — at which point the browser drops focus to `<body>` and the
   * reader's place on the page is gone.
   */
  const returnTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return

    returnTo.current = document.activeElement as HTMLElement | null

    /*
     * Focus the panel itself rather than its first link.
     *
     * 🔴 FIRST-LINK IS THE OBVIOUS CHOICE AND IT IS WRONG HERE. The first item
     * in every one of these navs is a destination — "Overview", "Home" — and
     * focusing it means a screen reader opens the menu by announcing one item
     * out of seven, with no indication that a menu was opened at all. The panel
     * carries `role="dialog"` and the label, so focusing it announces the menu,
     * and the first Tab then reaches the first item.
     */
    panel.current?.focus()
    document.documentElement.classList.add('scroll-locked')

    return () => {
      document.documentElement.classList.remove('scroll-locked')
      /*
       * ⚠️ GUARDED, BECAUSE THE TRIGGER MAY BE GONE. The menu button is
       * `lg:hidden`; if the drawer closed because the window was widened past
       * `lg`, the element that opened it is no longer in the document and
       * `focus()` on it is a silent no-op that leaves focus on `<body>`.
       */
      const back = returnTo.current
      if (back?.isConnected) back.focus()
    }
  }, [open])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return

      const items = panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE)
      if (!items || items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]

      /* The trap: wrap at both ends rather than letting focus escape the panel. */
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },
    [onClose],
  )

  if (!open) return null

  return (
    /*
     * ⚠️ `fixed inset-0`, NOT `absolute`. The shells are `h-dvh` flex rows with
     * their own stacking contexts; an absolutely-positioned drawer would be
     * bounded by whichever of them happened to be `relative` and would slide in
     * beside the content rather than over it.
     *
     * 🔴 NO `lg:hidden` HERE, AND ITS ABSENCE IS DELIBERATE. A class would hide
     * the panel on a widened window while `open` stayed `true` — so the scroll
     * lock would still be applied, focus would still be trapped inside an
     * invisible dialog, and Escape would be the only way out of a page that
     * looks entirely normal. The shells close it on the breakpoint instead,
     * which unwinds all four behaviours together. See `DashboardShell`.
     */
    <div className="fixed inset-0 z-50" onKeyDown={onKeyDown}>
      {/*
        The backdrop. It is a `button` rather than a `div` with an `onClick`
        because tapping outside to dismiss is the gesture every phone reader
        already has — but `aria-hidden` keeps it out of the accessibility tree,
        where a full-screen unlabelled button is noise. Escape is the equivalent
        for anyone not using a pointer.
      */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="drawer-backdrop tap-exempt absolute inset-0 h-full w-full cursor-default bg-ink/40 backdrop-blur-[2px]"
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={
          side === 'bottom'
            ? 'drawer-panel drawer-panel-bottom safe-px safe-pb-3 absolute inset-x-0 bottom-0 flex max-h-[85dvh] flex-col overflow-y-auto rounded-t-lg bg-surface shadow-raised outline-none'
            : `drawer-panel absolute inset-y-0 ${
                side === 'left' ? 'left-0' : 'right-0'
              } ${side === 'right' ? 'drawer-panel-right ' : ''}${width} safe-px flex max-w-[85vw] flex-col bg-surface shadow-raised outline-none`
        }
      >
        {children}
      </div>
    </div>
  )
}

export default Drawer
